<template>
    <div>
        <el-dialog
            :title="title"
            width="1000px"
            :visible="show"
            :before-close="beforeClose"
            :destroy-on-close="true"
            @close="onCancelClick"
            :fullscreen="true"
        >
            <el-form
                :model="form"
                ref="form"
                label-width="120px"
                label-position="right"
                :rules="rules"
                v-loading="processing"
            >
                <el-form-item label="Reference" prop="reference">
                    <el-input v-model="form.reference" autocomplete="off" clearable></el-input>
                </el-form-item>
                <el-row>
                    <el-col :span="10">
                        <el-form-item label="Create Date" prop="create_date">
                            <el-date-picker
                                v-model="form.create_date"
                                type="date"
                                format="dd/MM/yyyy"
                                value-format="yyyy-MM-dd"
                                placeholder="Select Date"
                            ></el-date-picker>
                        </el-form-item>
                    </el-col>
                    <el-col :span="10">
                        <el-form-item label="Currency" prop="asset_name">
                            <el-select
                                v-model="form.asset_name"
                                placeholder="Please select currency"
                            >
                                <el-option
                                    v-for="name in asset_names"
                                    :key="name"
                                    :label="name"
                                    :value="name"
                                ></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>
            <div class="parties">
                <div class="payer-list">
                    <el-button size="small" class="add-new-role-btn" @click="onNewPayerClick" :disabled="form.asset_name.length==0">Add a new payer</el-button>
                    <el-table
                        :data="form.payers"
                        style="width: 100%"
                        stripe
                        size="mini"
                        row-class-name="user-list-item"
                        show-summary
                        sum-text="Total"
                    >
                        <el-table-column prop="display_name" label="Name" width="200" fixed="left"></el-table-column>
                        <el-table-column prop="net_amount" label="Net amount" width="100"></el-table-column>
                        <el-table-column prop="fee_amount" label="Fee" width="100"></el-table-column>
                        <el-table-column prop="total_amount" label="Total"></el-table-column>
                        <el-table-column align="right" width="60">
                            <template slot-scope="scope">
                                <el-button size="mini"  icon="el-icon-delete" type="danger" @click="onDeletePayer(scope.$index, scope.row)"></el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                    <el-upload
                        action="#"
                        class="upload-csv"
                        :auto-upload="false"
                        :show-file-list="false"
                        :on-change="(file)=> {onUploaded('payers', file);}">
                        <el-button size="small" type="primary">Upload Payers CSV</el-button>
                    </el-upload>
                </div>
                <div class="receiver-list">
                    <el-button size="small" class="add-new-role-btn" @click="onNewReceiverClick" :disabled="form.asset_name.length==0">Add a new receiver</el-button>
                    <el-table
                        :data="form.receivers"
                        style="width: 100%"
                        stripe
                        size="mini"
                        row-class-name="user-list-item"
                        show-summary
                        sum-text="Total"
                    >
                        <el-table-column prop="display_name" label="Name" width="200" fixed="left"></el-table-column>
                        <el-table-column prop="total_amount" label="Total" width="100"></el-table-column>
                        <el-table-column prop="fee_amount" label="Fee" width="100"></el-table-column>
                        <el-table-column prop="net_amount" label="Net amount"></el-table-column>
                        <el-table-column align="right" width="60">
                            <template slot-scope="scope">
                                <el-button size="mini" icon="el-icon-delete" type="danger" @click="onDeleteReceiver(scope.$index, scope.row)"></el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                    <el-upload
                        action="#"
                        class="upload-csv"
                        :auto-upload="false"
                        :show-file-list="false"
                        :on-change="(file)=> {onUploaded('receivers', file);}">
                        <el-button size="small" type="primary">Upload Receivers CSV</el-button>
                    </el-upload>
                </div>
            </div>
            <el-alert
                v-if="payersTotalNetAmount == 0 || receiversTotalAmount == 0"
                title="At least one payer and one receiver required for a transfer."
                type="warning"
                show-icon>
            </el-alert>
            <el-alert
                v-if="!isPayReceiveEqual"
                title="Transfer Amount Not Match"
                type="error"
                description="Payers' net amount must equal to receivers' total amount."
                :closable="false"
                show-icon>
            </el-alert>
                
            <div slot="footer" class="dialog-footer" v-loading="processing">
                <el-button @click="onCancelClick">Cancel</el-button>
                <el-button @click="onSubmit" type="primary" :disabled="!readyToSubmit">Save</el-button>
            </div>
        </el-dialog>
        <NewPayer v-if="form.asset_name.length>0" :onSelect="onPayerSelect" :onClose="onNewPayerClose" :show="showNewPayer" :asset_name="form.asset_name" />
        <NewReceiver v-if="form.asset_name.length>0" :onSelect="onReceiverSelect" :onClose="onNewReceiverClose" :show="showNewReceiver" :asset_name="form.asset_name" />
    </div>
</template>

<script>
import { mapActions } from "vuex";
import NewPayer from "./TransferNewPayers";
import NewReceiver from "./TransferNewReceiver";
import Big from 'big.js';

export default {
    name: "TransferNew",
    props: {
        data: Object,
        show: { default: false },
        onClose: Function
    },
    components: {
        NewPayer,
        NewReceiver
    },
    data() {
        return {
            form: {
                reference: "",
                create_date: "",
                asset_name: "",
                payers: [],
                receivers: []
            },
            asset_names: [],
            showNewPayer: false,
            showNewReceiver: false,
            fullscreen: false,
            processing: false,
            processing_search: false,
            rules: {
                reference: [
                    {
                        required: true,
                        message: "Reference is required",
                        trigger: "blur"
                    }
                ],
                create_date: [
                    {
                        required: true,
                        message: "Create Date is required",
                        trigger: "blur"
                    }
                ],
                asset_name: [
                    {
                        required: true,
                        message: "Currency is required",
                        trigger: "blur"
                    }
                ]
            }
        };
    },
    watch: {
        data(newData) {
            if (newData) {
                this.init(newData);
            }
        },
        show(show){
            if(show){
                this.form.reference = `${Date.now()}`;
                this.form.create_date = "",
                this.form.asset_name = "",
                this.form.payers = [],
                this.form.receivers = []
            }
        }
    },
    mounted() {
        this.loadCurrenciesData();
    },
    computed: {
        title() {
            if (!this.data) {
                return "Create a new transfer";
            }
            return `Edit ${this.data.display_name ||
                this.data.email} transfer`;
        },
        payersTotalNetAmount() {
            let total = 0;
            for(let {net_amount} of this.form.payers) {
                total += Number(net_amount);
            }
            return total;
        },
        receiversTotalAmount() {
            let total = 0;
            for(let {total_amount} of this.form.receivers) {
                total += Number(total_amount);
            }
            return total;
        },
        isPayReceiveEqual() {
            return this.payersTotalNetAmount == this.receiversTotalAmount;
        },
        readyToSubmit() {
            return this.payersTotalNetAmount > 0 && this.receiversTotalAmount > 0 && this.isPayReceiveEqual 
        }
    },
    methods: {
        ...mapActions(["request", "getError"]),
        beforeClose(done = () => {}) {
            this.reference = "",
            this.create_date = "",
            this.asset_name = "",
            this.payers = [],
            this.receivers = []
            done();
        },
        
        onCancelClick(reload = false) {
            this.form.reference = "",
            this.form.create_date = "",
            this.form.asset_name = "",
            this.form.payers = [],
            this.form.receivers = []
            this.onClose(reload);
        },
        loadCurrenciesData() {
            let ctrl = this;
            ctrl.processing = true;
            ctrl.request({
                method: "GET",
                endpoint: `currencies?limit=100&where=enabled=1`
            })
                .then(({ data = {} }) => {
                    ctrl.asset_names = data.results.map(d => {
                        return d.asset_name;
                    });
                })
                .catch(err => {
                    ctrl.getError({
                        err,
                        title: "CURRENCY_ERROR",
                        message: "Get currency Failed"
                    }).then(ctrl.$notify);
                })
                .finally(() => {
                    ctrl.processing = false;
                });
        },
        onNewPayerClick() {
            this.showNewPayer = true;
        },
        onPayerSelect(item) {
            if (
                this.form.payers.findIndex(u => {
                    return u.authenticate_id == item.authenticate_id;
                }) < 0
            ) {
                this.form.payers.push(item);
            }
        },
        onNewPayerClose() {
            this.showNewPayer = false;
        },
        onDeletePayer(index) {
            this.form.payers.splice(index, 1);
        },

        onNewReceiverClick() {
            this.showNewReceiver = true;
        },
        onReceiverSelect(item) {
            if (
                this.form.receivers.findIndex(u => {
                    return u.authenticate_id == item.authenticate_id;
                }) < 0
            ) {
                this.form.receivers.push(item);
            }
        },
        onNewReceiverClose() {
            this.showNewReceiver = false;
        },
        onDeleteReceiver(index) {
            this.form.receivers.splice(index, 1);
        },
        
        onSubmit() {
            let ctrl = this;
            ctrl.processing = true;
            ctrl.request({
                method: "POST",
                endpoint: `transfer`,
                data: ctrl.form
            })
                .then(() => {
                    this.$message({
                        message: "Transfer completed",
                        type: "success"
                    });
                    ctrl.onCancelClick(true);
                })
                .catch(err => {
                    ctrl.getError({
                        err,
                        title: "TRANSFER_ERROR",
                        message: "Transfer Failed"
                    }).then(ctrl.$notify);
                })
                .finally(() => {
                    ctrl.processing = false;
                });
        },
        onFullscreenClick(){
            this.fullscreen = !this.fullscreen;
        },
        onUploaded(target = 'payers', file) {
            let ctrl = this;
            if(file) {
                var reader = new FileReader();
                reader.readAsText(file.raw);
                reader.onload = function (evt) {
                    var data = ctrl.csvJSON(evt.target.result);
                    if(data.length > 0) {
                        ctrl.form[target] = [];
                    }
                    for(let d of data) {
                        if(d.payer && d.payer.trim().length > 0) {
                            if(target == 'payers') {
                                if(isNaN(d.net_amount)) {
                                    ctrl.getError({
                                        title: "CSV data error",
                                        message: "Invalid net_amount"
                                    }).then(ctrl.$notify);
                                }
                                if(isNaN(d.fee_amount)) {
                                    ctrl.getError({
                                        title: "CSV data error",
                                        message: "Invalid fee_amount"
                                    }).then(ctrl.$notify);
                                }
                            }
                            ctrl.searchUser(d.payer, (users) => {
                                if(users.length > 0) {
                                    let user = users[0];
                                    ctrl.form[target].push({
                                        authenticate_id: user.authenticate_id,
                                        display_name: user.display_name,
                                        net_amount: d.net_amount,
                                        fee_amount: d.fee_amount,
                                        total_amount: Number((new Big(d.net_amount).plus(new Big(d.fee_amount)))),
                                    });
                                }
                            });
                        }
                        if(d.payee && d.payee.trim().length > 0) {
                            if(target == 'payee') {
                                if(isNaN(d.total_amount)) {
                                    ctrl.getError({
                                        title: "CSV data error",
                                        message: "Invalid total_amount"
                                    }).then(ctrl.$notify);
                                }
                                if(isNaN(d.fee_amount)) {
                                    ctrl.getError({
                                        title: "CSV data error",
                                        message: "Invalid fee_amount"
                                    }).then(ctrl.$notify);
                                }
                            }
                            ctrl.searchUser(d.payee, (users) => {
                                if(users.length > 0) {
                                    let user = users[0];
                                    ctrl.form[target].push({
                                        authenticate_id: user.authenticate_id,
                                        display_name: user.display_name,
                                        total_amount: d.total_amount,
                                        fee_amount: d.fee_amount,
                                        net_amount: Number((new Big(d.total_amount).minus(new Big(d.fee_amount)))),
                                    });
                                }
                            });
                        }
                    }
                } 
            }
        },
        csvJSON(csv){
            var lines=csv.split('\r\n');
            var result = [];
            var headers=lines[0].split(",");
            for(var i=1;i<lines.length;i++){
                var obj = {};
                var currentline=lines[i].split(",");
                for(var j=0;j<headers.length;j++){
                    obj[headers[j]] = currentline[j];
                }
                result.push(obj);
            }
            return result;
        },
        searchUser(queryString = "", callback = () => {}) {
            let ctrl = this;
            if (!queryString || queryString.trim().length == 0) {
                callback([]);
                return;
            }
            ctrl.processing_search = true;
            let query = `authenticate?
                limit=10&where=activated=1 
                AND disabled=0 `;
            if (queryString.trim().length > 0) {
                query += ` AND (email='${queryString}') `;
            }

            let endpoint = encodeURI(query);

            ctrl.request({
                method: "GET",
                endpoint
            })
                .then(({ data = {} }) => {
                    callback(data.results);
                })
                .catch(err => {
                    ctrl.getError({
                        err,
                        title: "PERMISSION_ERROR",
                        message: "Get permission level Failed"
                    }).then(ctrl.$notify);
                })
                .finally(() => {
                    ctrl.processing_search = false;
                });
        },
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
<style>
.authenticate-autocomplete li {
    line-height: 1.5em;
    padding: 7px;
}

.authenticate-autocomplete .display_name {
    text-overflow: ellipsis;
    overflow: hidden;
}
.authenticate-autocomplete .email {
    font-size: 12px;
    color: #b4b4b4;
}

.highlighted .email {
    color: #ddd;
}

.parties {
    display: flex;
}

.parties > div{
    width: 50%;
    padding: 8px;
}
.user-list-item .cell {
    white-space: nowrap;
}

.upload-csv {
    margin-top: 16px;
}
.upload-csv .el-upload {
    display: block;
}
</style>